import React, { MouseEvent, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Clear, FileDownloadOutlined, ViewColumn } from '@mui/icons-material';
import {
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  ToolbarButton,
  gridColumnVisibilityModelSelector,
  useGridSelector,
  gridExpandedSortedRowIdsSelector,
  gridVisibleColumnDefinitionsSelector,
  useGridApiContext,
} from '@mui/x-data-grid';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { ExcelCell, exportToExcel } from 'helpers/exportToExcel';

export type ColumnItem = { field: string; label: string };

export type ExportCellGetter = (field: string, row: any) => ExcelCell;

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    fileName?: string;
    sheetName?: string;
    getExportCell?: ExportCellGetter;
    columnItems?: ColumnItem[];
    onMoveColumn?: (field: string, step: -1 | 1) => void;
  }
}

type DataGridToolbarProps = {
  fileName?: string;
  sheetName?: string;
  getExportCell?: ExportCellGetter;
  columnItems?: ColumnItem[];
  onMoveColumn?: (field: string, step: -1 | 1) => void;
};

const DataGridToolbar: React.FC<DataGridToolbarProps> = ({
  fileName = 'lista',
  sheetName = 'Lista',
  getExportCell,
  columnItems = [],
  onMoveColumn,
}) => {
  const apiRef = useGridApiContext();
  const [exporting, setExporting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const visibility = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  // Exporta o que o usuário está vendo: colunas visíveis, na ordem em que aparecem, e linhas já ordenadas/filtradas.
  async function handleExport() {
    if (!getExportCell) return;

    const columns = gridVisibleColumnDefinitionsSelector(apiRef).filter(column => !column.disableExport);
    const rows = gridExpandedSortedRowIdsSelector(apiRef).map(id => apiRef.current.getRow(id));

    setExporting(true);

    try {
      await exportToExcel({
        fileName: `${fileName}_${format(new Date(), 'yyyy-MM-dd')}`,
        sheetName,
        columns: columns.map(column => ({
          header: column.headerName ?? column.field,
          width: column.width,
          align: column.align === 'right' ? 'right' : undefined,
        })),
        rows: rows.map(row => columns.map(column => getExportCell(column.field, row))),
      });
    } catch {
      toast.error('Não foi possível exportar para Excel');
    } finally {
      setExporting(false);
    }
  }

  return (
    <Toolbar>
      <Tooltip title="Colunas: mostrar, ocultar e reordenar">
        <ToolbarButton onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}>
          <ViewColumn fontSize="small" />
        </ToolbarButton>
      </Tooltip>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {columnItems.map((item, index) => (
          <MenuItem
            key={item.field}
            disableRipple
            onClick={() => apiRef.current.setColumnVisibility(item.field, visibility[item.field] === false)}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0 }}
          >
            <Typography variant="body2">{item.label}</Typography>
            <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                aria-label="Mover para cima"
                disabled={index === 0}
                onClick={() => onMoveColumn?.(item.field, -1)}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Mover para baixo"
                disabled={index === columnItems.length - 1}
                onClick={() => onMoveColumn?.(item.field, 1)}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </span>
            <Switch size="small" color="primary" checked={visibility[item.field] !== false} />
          </MenuItem>
        ))}
      </Menu>
      <Button
        size="small"
        onClick={handleExport}
        disabled={exporting || !getExportCell}
        startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadOutlined fontSize="small" />}
      >
        Exportar Excel
      </Button>
      <QuickFilter expanded style={{ marginLeft: 'auto' }}>
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <TextField
              {...controlProps}
              inputRef={ref}
              size="small"
              placeholder="Buscar"
              sx={{ width: 160, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
              slotProps={{
                input: {
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear render={<IconButton size="small" aria-label="Limpar busca" />}>
                        <Clear fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          )}
        />
      </QuickFilter>
    </Toolbar>
  );
};

export default DataGridToolbar;
